import { get, set } from 'idb-keyval';
import { logger } from 'logger';

interface IdbCacheEntry<T> {
  data: T;
  ts: number;
  meta: Record<string, string | number>;
}

function metaMatches(a: Record<string, string | number>, b: Record<string, string | number>): boolean {
  const ka = Object.keys(a);
  const kb = Object.keys(b);
  return ka.length === kb.length && ka.every((k) => a[k] === b[k]);
}

export async function readIdbCache<T>(
  key: string,
  ttlMs: number,
  meta: Record<string, string | number>,
): Promise<T | null> {
  const entry = await readIdbCacheEntry<T>(key, ttlMs, meta);
  return entry ? entry.data : null;
}

export async function readIdbCacheEntry<T>(
  key: string,
  ttlMs: number,
  meta: Record<string, string | number>,
): Promise<{ data: T; ageMs: number } | null> {
  try {
    const entry = (await get(key)) as IdbCacheEntry<T> | undefined;
    if (!entry || !entry.ts || Date.now() - entry.ts > ttlMs || !metaMatches(entry.meta || {}, meta)) {
      return null;
    }
    if (entry.data == null) {
      return null;
    }
    return { data: entry.data, ageMs: Date.now() - entry.ts };
  } catch (e) {
    logger.warn(`idbCache: read "${key}" failed`, e);
    return null;
  }
}

export async function writeIdbCache<T>(key: string, data: T, meta: Record<string, string | number>): Promise<void> {
  try {
    await set(key, { data, ts: Date.now(), meta } as IdbCacheEntry<T>);
  } catch (e) {
    logger.warn(`idbCache: write "${key}" failed`, e);
  }
}
